import { useCallback } from "react";
import { Form, Formik } from "formik";
import { ApiKeyField } from "@app/components/forms/apiKeyField";
import { SubmitButton } from "@app/components/forms/submitButton";
import { SelectField } from "@app/components/forms/selectField";
import { findDuplicates } from "@app/tools/leads/findLeadDuplicates/findLeadDuplicatesHelper";
import { trackAnalyticsAndRunTool } from "@app/segment/analytics";
import { useOutputProviderContext } from "@app/context/useOutputProviderContext";
import type { Tool } from "@app/internal/supportToolsList/toolsList";
import { ToolHeader } from "@app/components/common/toolHeader";

export interface FormValues {
  apiKey: string;
  deduplicateBy:
    | "lead_name"
    | "email_address"
    | "phone_number"
    | "email_domain"
    | "url"
    | "url_domain"
    | "contact_name"
    | "address_1"
    | string;
}

export default function FindLeadDuplicatesPage(props: { tool: Tool }) {
  const { addOutputLine, clearOutput } = useOutputProviderContext();

  return (
    <>
      <ToolHeader tool={props.tool} />
      <p className="mb-4">
        You can see an example of the generated CSV file{" "}
        <a
          target="_blank"
          href="/example-files/find-lead-duplicates/ACME%20Inc%20Lead%20name%20Duplicates.csv"
          className="text-blue-600 hover:underline hover:text-blue-800 visited:text-purple-600"
        >
          here
        </a>
        .
      </p>
      <h3 className="uppercase font-bold">Lead merging</h3>
      <p className="mb-4">
        This tool will not merge or modify any leads in your Close account. If
        you want to merge all the duplicates in bulk, please send us the
        generated CSV file to{" "}
        <a
          className="text-blue-600 hover:underline hover:text-blue-800 visited:text-purple-600"
          href="mailto:support@close.com"
        >
          support@close.com
        </a>{" "}
        and specify how should we merge them (e.g. prioritize the older lead)
        and we will take care of that for you. If conflicts exist in the lead
        name, description, status, or a custom field, we will keep the data from
        the prioritized lead.
      </p>
      <Formik<FormValues>
        initialValues={{
          apiKey: "",
          deduplicateBy: "lead_name",
        }}
        onSubmit={useCallback(
          async (values) => {
            await trackAnalyticsAndRunTool(
              props.tool.name,
              values,
              async () => {
                await findDuplicates(values, addOutputLine, clearOutput);
                return true;
              },
            );
          },
          [props, addOutputLine, clearOutput],
        )}
      >
        <Form className="flex flex-col gap-6">
          <ApiKeyField field="apiKey" permissions={["export"]} />
          <SelectField
            id="deduplicateBy"
            name="deduplicateBy"
            label="Find duplicates by"
            options={[
              {
                value: "lead_name",
                label: "Lead Name",
              },
              {
                value: "url",
                label: "Lead URL",
              },
              {
                value: "url_domain",
                label: "Lead URL Domain",
              },
              {
                value: "address_1",
                label: "Lead Address (Street)",
              },
              {
                value: "contact_name",
                label: "Contact Name",
              },
              {
                value: "phone_number",
                label: "Contact Phone number",
              },
              {
                value: "email_address",
                label: "Contact Email Address",
              },
              {
                value: "email_domain",
                label: "Contact Email Address Domain",
              },
            ].map((choice) => ({
              value: choice.value,
              label: choice.label,
            }))}
          />
          <SubmitButton>Find Duplicates (CSV)</SubmitButton>
        </Form>
      </Formik>
    </>
  );
}
